import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/Button';
import { useAuthStore } from '@/store/auth';
import {
  listVitals, syncVitals, probeReportingApi,
  type PlayVitalsRow, type ApiProbeRow,
} from '@/api/play';

// Google's bad-behaviour thresholds: crossing either one suppresses store
// visibility, so they are the only colouring that matters here.
const CRASH_THRESHOLD = 0.0109;
const ANR_THRESHOLD = 0.0047;

const fmtUnix = (s: number | null) => (s ? new Date(s * 1000).toLocaleString() : '—');

function RateCell({ value, threshold }: { value: number | null; threshold: number }) {
  if (value == null) return <span className="text-content-muted">—</span>;
  const over = value > threshold;
  return (
    <span
      className={over ? 'text-red-400 font-medium' : 'text-emerald-400'}
      title={over
        ? `Above Google's bad-behaviour threshold of ${(threshold * 100).toFixed(2)}%`
        : `Below Google's bad-behaviour threshold of ${(threshold * 100).toFixed(2)}%`}
    >
      {(value * 100).toFixed(2)}%
    </span>
  );
}

function StatusCell({ row }: { row: PlayVitalsRow }) {
  if (row.status === 'error') {
    return (
      <span className="text-red-400 max-w-[20rem] truncate inline-block align-bottom" title={row.error ?? ''}>
        {row.error ?? 'error'}
      </span>
    );
  }
  if (row.status === 'no_data') {
    return (
      <span className="text-content-muted" title="Google withholds vitals for apps below its privacy threshold">
        No data yet
      </span>
    );
  }
  if (!row.status) return <span className="text-content-muted">Never synced</span>;
  return <span className="text-content-muted">{fmtUnix(row.last_run_at)}</span>;
}

type SortKey = 'crash_rate' | 'anr_rate' | 'distinct_users' | 'package_name' | 'account_name' | 'date';

/** Nulls always sort last, whichever direction is active. */
function compare(a: PlayVitalsRow, b: PlayVitalsRow, key: SortKey, order: 'asc' | 'desc'): number {
  const av = a[key];
  const bv = b[key];
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  const cmp = typeof av === 'number' && typeof bv === 'number'
    ? av - bv
    : String(av).localeCompare(String(bv));
  return order === 'asc' ? cmp : -cmp;
}

export function PlayVitals() {
  const qc = useQueryClient();
  const [sortBy, setSortBy] = useState<SortKey>('crash_rate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const { data: rows = [], isLoading, error, refetch } = useQuery({
    queryKey: ['play-vitals'],
    queryFn: listVitals,
  });

  const [probe, setProbe] = useState<ApiProbeRow[] | null>(null);
  const [probeError, setProbeError] = useState<string | null>(null);
  const isSuper = useAuthStore((s) => s.admin?.role) === 'super_admin';

  // Read-only: asks Google for the API's state in each service account's own
  // project, and whether that account may switch it on itself.
  const probeMut = useMutation({
    mutationFn: (accountId?: number) => probeReportingApi(accountId),
    onSuccess: (r) => { setProbeError(null); setProbe(r); },
    onError: (e: any) => { setProbe(null); setProbeError(e?.message ?? 'Probe failed'); },
  });

  const syncMut = useMutation({
    mutationFn: syncVitals,
    onSuccess: (r) => {
      setSyncError(null);
      setSyncResult(`Synced ${r.synced}, no data ${r.noData}, failed ${r.failed}`);
      qc.invalidateQueries({ queryKey: ['play-vitals'] });
    },
    onError: (e: any) => { setSyncResult(null); setSyncError(e?.message ?? 'Sync failed'); },
  });

  const sorted = useMemo(
    () => [...rows].sort((a, b) => compare(a, b, sortBy, sortOrder)),
    [rows, sortBy, sortOrder],
  );

  const onSort = (key: string) => {
    if (key === sortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key as SortKey);
      setSortOrder('desc');
    }
  };

  // Google's two 403s mean completely different things and are fixed in
  // different consoles, so split them out and name the projects involved
  // rather than making someone read 40 truncated error strings.
  const denials = useMemo(() => {
    const projects = new Set<string>();
    let permission = 0;
    for (const r of rows) {
      const e = r.error ?? '';
      const m = /has not been used in project (\d+)/.exec(e);
      if (m?.[1]) projects.add(m[1]);
      else if (/does not have permission|PERMISSION_DENIED/.test(e)) permission += 1;
    }
    return { projects: [...projects].sort(), permission };
  }, [rows]);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-content">Android Vitals</h1>
          <p className="text-sm text-content-muted mt-1">
            User-perceived crash and ANR rates, 28-day rolling, straight from Play Console.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/play" className="text-sm text-accent hover:underline">Play accounts</Link>
          {isSuper && (
            <>
              <Button
                variant="secondary"
                onClick={() => probeMut.mutate(rows[0]?.account_id)}
                disabled={probeMut.isPending || rows.length === 0}
                title="Read-only: checks the API switch in one service account's project"
              >
                Check one account
              </Button>
              <Button
                variant="secondary"
                onClick={() => probeMut.mutate(undefined)}
                disabled={probeMut.isPending}
                title="Read-only: checks every account's project"
              >
                {probeMut.isPending ? 'Checking…' : 'Check all'}
              </Button>
            </>
          )}
          <Button onClick={() => syncMut.mutate()} disabled={syncMut.isPending}>
            {syncMut.isPending ? 'Refreshing…' : 'Refresh now'}
          </Button>
        </div>
      </div>

      {denials.projects.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-300">
          <p>
            The <span className="font-medium">Google Play Developer Reporting API</span> is not enabled in{' '}
            {denials.projects.length} GCP {denials.projects.length === 1 ? 'project' : 'projects'} — every service account
            lives in its own project, so it has to be switched on in each one.
          </p>
          <p className="mt-2 text-xs text-amber-300/80">
            Fastest way is one gcloud pass:{' '}
            <code className="font-mono">
              gcloud services enable playdeveloperreporting.googleapis.com --project=&lt;id&gt;
            </code>
          </p>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
            {denials.projects.map((p) => (
              <a
                key={p}
                href={`https://console.developers.google.com/apis/api/playdeveloperreporting.googleapis.com/overview?project=${p}`}
                target="_blank"
                rel="noreferrer"
                className="font-mono underline hover:no-underline"
              >
                {p}
              </a>
            ))}
          </div>
        </div>
      )}

      {denials.permission > 0 && (
        <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-300">
          {denials.permission} {denials.permission === 1 ? 'app has' : 'apps have'} the API enabled but the service account
          is denied. Publishing rights are not enough — in Play Console, under Users and permissions, grant that service
          account <span className="font-medium">View app information and download bulk reports</span>.
        </div>
      )}

      {probeError && (
        <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-400">{probeError}</div>
      )}

      {probe && (
        <div className="mb-4 rounded-lg border border-border bg-surface p-4 text-sm">
          <p className="text-content mb-2">
            API access check — {probe.filter((p) => p.can_enable).length} of {probe.length} service{' '}
            {probe.length === 1 ? 'account can' : 'accounts can'} switch the API on without anyone signing in.
          </p>
          <div className="max-h-72 overflow-y-auto">
            {probe.map((p) => (
              <div key={p.account_id} className="flex flex-wrap items-baseline gap-x-3 py-1 border-t border-border/60">
                <span className="text-content">{p.account_name}</span>
                <span className="font-mono text-xs text-content-muted">{p.project_id ?? 'no project id'}</span>
                <span className={p.api_state === 'ENABLED' ? 'text-emerald-400 text-xs' : 'text-content-muted text-xs'}>
                  {p.api_state ?? 'state unknown'}
                </span>
                <span className={p.can_enable ? 'text-emerald-400 text-xs' : 'text-amber-400 text-xs'}>
                  {p.can_enable ? 'can enable' : 'cannot enable'}
                </span>
                {p.error && (
                  <span className="text-red-400 text-xs max-w-[28rem] truncate" title={p.error}>{p.error}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {syncError && (
        <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-400">{syncError}</div>
      )}
      {syncResult && (
        <div className="mb-4 rounded-lg border border-border bg-surface p-4 text-sm text-content-muted">{syncResult}</div>
      )}

      {error ? (
        <div className="bg-surface border border-border rounded-lg p-6 text-center">
          <p className="text-sm text-red-400 mb-3">{(error as Error).message}</p>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : (
        <DataTable<PlayVitalsRow>
          loading={isLoading}
          data={sorted}
          keyExtractor={(r) => String(r.app_id)}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
          columns={[
            {
              key: 'package_name', label: 'App', sortKey: 'package_name',
              render: (r) => (
                <span className="flex flex-col">
                  <span className="text-content">{r.title || r.package_name}</span>
                  {r.title && <span className="text-xs text-content-muted">{r.package_name}</span>}
                </span>
              ),
            },
            {
              key: 'account_name', label: 'Account', sortKey: 'account_name',
              render: (r) => (
                <Link to={`/play/${r.account_id}`} className="text-accent hover:underline">{r.account_name}</Link>
              ),
            },
            {
              key: 'crash_rate', label: 'Crash rate', sortKey: 'crash_rate',
              render: (r) => <RateCell value={r.crash_rate} threshold={CRASH_THRESHOLD} />,
            },
            {
              key: 'anr_rate', label: 'ANR rate', sortKey: 'anr_rate',
              render: (r) => <RateCell value={r.anr_rate} threshold={ANR_THRESHOLD} />,
            },
            {
              key: 'distinct_users', label: 'Users', sortKey: 'distinct_users',
              render: (r) => (
                <span className="text-content-muted">
                  {r.distinct_users == null ? '—' : r.distinct_users.toLocaleString('en-US')}
                </span>
              ),
            },
            {
              key: 'date', label: 'Data as of', sortKey: 'date',
              render: (r) => (
                <span className="text-content-muted" title="Calendar day in America/Los_Angeles, as reported by Google">
                  {r.date ?? '—'}
                </span>
              ),
            },
            { key: 'status', label: 'Last sync', render: (r) => <StatusCell row={r} /> },
          ]}
        />
      )}
    </div>
  );
}
